import { useState, useEffect, useCallback } from "react";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Zap,
  Clock,
  FileText,
  DollarSign,
  ArrowRight,
  Users,
  Bell,
  Mail,
  Phone,
  Star,
  Check,
  TrendingUp,
  Play,
  Pause,
} from "lucide-react";

const AUTOPLAY_INTERVAL = 6000;
const TOTAL_STEPS = 4;

const ProgressBar = ({ current, total, isPlaying, onStepClick }) => (
  <div className="flex items-center gap-2">
    {Array.from({ length: total }).map((_, i) => (
      <button
        key={i}
        onClick={() => onStepClick(i)}
        className="relative h-1.5 flex-1 rounded-full overflow-hidden bg-slate-200 cursor-pointer"
        data-testid={`demo-progress-${i}`}
      >
        <div
          className={`absolute inset-0 rounded-full transition-all duration-300 ${
            i < current
              ? "bg-[#3B82F6] w-full"
              : i === current
              ? "bg-[#3B82F6]"
              : "bg-transparent"
          }`}
          style={{
            width: i < current ? "100%" : i === current && isPlaying ? "100%" : i === current ? "30%" : "0%",
            transition: i === current && isPlaying ? `width ${AUTOPLAY_INTERVAL}ms linear` : "width 300ms ease",
          }}
        />
      </button>
    ))}
  </div>
);

// Step 1: Dashboard with Revenue Radar + Follow-Up Queue
const DashboardStep = () => (
  <div className="space-y-4 animate-fadeIn">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-gradient-to-br from-[#6366F1]/5 to-[#6366F1]/10 rounded-xl border border-[#6366F1]/20 p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-[#6366F1] rounded-lg flex items-center justify-center">
            <Zap className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-slate-900 text-sm">Revenue Radar</span>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-slate-500 text-sm">Clients need attention</span>
            <span className="text-2xl font-bold text-slate-900">3</span>
          </div>
          <div className="h-px bg-[#6366F1]/20" />
          <div className="flex items-center justify-between">
            <span className="text-slate-500 text-sm">Potential revenue</span>
            <span className="text-2xl font-bold text-[#6366F1]">$2,250</span>
          </div>
          <div className="h-px bg-[#6366F1]/20" />
          <div className="flex items-center justify-between">
            <span className="text-slate-500 text-sm">Avg. days since contact</span>
            <span className="text-2xl font-bold text-slate-900">14</span>
          </div>
        </div>
      </div>

      <div className="md:col-span-2 bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#22C55E] rounded-lg flex items-center justify-center">
              <Clock className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-slate-900 text-sm">Today's Follow-Up Queue</span>
          </div>
          <span className="text-xs text-slate-400">3 clients</span>
        </div>
        <div className="space-y-2.5">
          {[
            { name: "Corey Gomez", initials: "CG", status: "Overdue", statusColor: "text-red-600", bgColor: "bg-red-50", borderColor: "border-red-100", amount: "$750" },
            { name: "Maria Lopez", initials: "ML", status: "Due Today", statusColor: "text-[#F59E0B]", bgColor: "bg-[#F59E0B]/10", borderColor: "border-[#F59E0B]/20", amount: "$1,200" },
            { name: "Jose Soto", initials: "JS", status: "Upcoming", statusColor: "text-[#22C55E]", bgColor: "bg-[#22C55E]/10", borderColor: "border-[#22C55E]/20", amount: "$300" },
          ].map((client, i) => (
            <div key={i} className={`flex items-center justify-between p-3 ${client.bgColor} rounded-lg border ${client.borderColor}`}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-xs font-medium text-slate-600">
                  {client.initials}
                </div>
                <div>
                  <span className="font-medium text-slate-900 text-sm">{client.name}</span>
                  <p className="text-xs text-slate-400">{client.amount} outstanding</p>
                </div>
              </div>
              <span className={`text-xs font-medium ${client.statusColor} px-2 py-1 rounded-full bg-white/60`}>
                {client.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Step 2: Follow-Up Queue Detail
const FollowUpStep = () => (
  <div className="space-y-4 animate-fadeIn">
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#22C55E] rounded-lg flex items-center justify-center">
            <Bell className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-slate-900">Follow-Up Queue</span>
        </div>
        <div className="flex gap-2">
          <span className="text-xs font-medium text-red-600 bg-red-50 px-2.5 py-1 rounded-full">1 Overdue</span>
          <span className="text-xs font-medium text-[#F59E0B] bg-[#F59E0B]/10 px-2.5 py-1 rounded-full">2 Due Today</span>
          <span className="text-xs font-medium text-[#22C55E] bg-[#22C55E]/10 px-2.5 py-1 rounded-full">4 Upcoming</span>
        </div>
      </div>

      <div className="space-y-3">
        {[
          { name: "Corey Gomez", category: "Insurance", lastContact: "12 days ago", status: "Overdue", statusColor: "text-red-600", bgColor: "bg-red-50", borderColor: "border-red-100", actions: ["Call", "Email"] },
          { name: "Maria Lopez", category: "Real Estate", lastContact: "7 days ago", status: "Due Today", statusColor: "text-[#F59E0B]", bgColor: "bg-[#F59E0B]/5", borderColor: "border-[#F59E0B]/20", actions: ["Email", "Note"] },
          { name: "Tanya Richards", category: "Consulting", lastContact: "5 days ago", status: "Due Today", statusColor: "text-[#F59E0B]", bgColor: "bg-[#F59E0B]/5", borderColor: "border-[#F59E0B]/20", actions: ["Call"] },
          { name: "Jose Soto", category: "Freelance", lastContact: "3 days ago", status: "Tomorrow", statusColor: "text-[#22C55E]", bgColor: "bg-[#22C55E]/5", borderColor: "border-[#22C55E]/20", actions: ["Email"] },
          { name: "Aisha Patel", category: "Agency", lastContact: "2 days ago", status: "In 3 days", statusColor: "text-[#3B82F6]", bgColor: "bg-[#3B82F6]/5", borderColor: "border-[#3B82F6]/20", actions: ["Call", "Email"] },
        ].map((client, i) => (
          <div key={i} className={`flex items-center justify-between p-3.5 ${client.bgColor} rounded-lg border ${client.borderColor}`}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-slate-200 rounded-full flex items-center justify-center text-xs font-semibold text-slate-600">
                {client.name.split(" ").map(n => n[0]).join("")}
              </div>
              <div>
                <span className="font-medium text-slate-900 text-sm">{client.name}</span>
                <p className="text-xs text-slate-400">{client.category} &middot; Last contact: {client.lastContact}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {client.actions.map((action, j) => (
                <span key={j} className="flex items-center gap-1 text-xs text-slate-500 bg-white px-2 py-1 rounded border border-slate-200">
                  {action === "Call" && <Phone className="w-3 h-3" />}
                  {action === "Email" && <Mail className="w-3 h-3" />}
                  {action === "Note" && <FileText className="w-3 h-3" />}
                  {action}
                </span>
              ))}
              <span className={`text-xs font-medium ${client.statusColor} ml-2`}>{client.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Step 3: Client Profile / Activity Timeline
const ClientProfileStep = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-fadeIn">
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="text-center mb-4">
        <div className="w-14 h-14 bg-[#3B82F6]/10 rounded-full flex items-center justify-center mx-auto mb-3">
          <span className="text-lg font-bold text-[#3B82F6]">CG</span>
        </div>
        <h4 className="font-semibold text-slate-900">Corey Gomez</h4>
        <p className="text-xs text-slate-400">Insurance &middot; Client since Mar 2024</p>
      </div>
      <div className="space-y-3 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-slate-500">Total Revenue</span>
          <span className="font-semibold text-slate-900">$4,500</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-slate-500">Outstanding</span>
          <span className="font-semibold text-[#F59E0B]">$750</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-slate-500">Interactions</span>
          <span className="font-semibold text-slate-900">24</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-slate-500">Rating</span>
          <div className="flex gap-0.5">
            {[1,2,3,4,5].map(s => <Star key={s} className={`w-3.5 h-3.5 ${s <= 4 ? "text-[#F59E0B] fill-[#F59E0B]" : "text-slate-200"}`} />)}
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-5">
        <button className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium text-white bg-[#3B82F6] rounded-lg py-2 hover:bg-[#2563EB] transition-colors">
          <Phone className="w-3 h-3" /> Call
        </button>
        <button className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium text-slate-700 bg-slate-100 rounded-lg py-2 hover:bg-slate-200 transition-colors">
          <Mail className="w-3 h-3" /> Email
        </button>
      </div>
    </div>

    <div className="md:col-span-2 bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-[#3B82F6] rounded-lg flex items-center justify-center">
          <FileText className="w-4 h-4 text-white" />
        </div>
        <span className="font-semibold text-slate-900 text-sm">Activity Timeline</span>
      </div>
      <div className="relative">
        <div className="absolute left-4 top-0 bottom-0 w-px bg-slate-200" />
        <div className="space-y-5">
          {[
            { date: "Today", event: "Follow-up reminder triggered", type: "reminder", color: "#F59E0B" },
            { date: "Mar 5", event: "Invoice #1042 sent — $750", type: "invoice", color: "#F59E0B" },
            { date: "Feb 28", event: "Phone call — discussed renewal options", type: "call", color: "#3B82F6" },
            { date: "Feb 20", event: "Email sent — proposal attached", type: "email", color: "#6366F1" },
            { date: "Feb 14", event: "Meeting note added", type: "note", color: "#22C55E" },
            { date: "Jan 30", event: "Payment received — $1,500", type: "payment", color: "#22C55E" },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-4 pl-1">
              <div className="w-7 h-7 rounded-full border-2 border-white flex items-center justify-center flex-shrink-0 z-10" style={{ backgroundColor: `${item.color}15`, borderColor: `${item.color}30` }}>
                {item.type === "reminder" && <Bell className="w-3 h-3" style={{ color: item.color }} />}
                {item.type === "invoice" && <DollarSign className="w-3 h-3" style={{ color: item.color }} />}
                {item.type === "call" && <Phone className="w-3 h-3" style={{ color: item.color }} />}
                {item.type === "email" && <Mail className="w-3 h-3" style={{ color: item.color }} />}
                {item.type === "note" && <FileText className="w-3 h-3" style={{ color: item.color }} />}
                {item.type === "payment" && <Check className="w-3 h-3" style={{ color: item.color }} />}
              </div>
              <div>
                <p className="text-sm text-slate-900">{item.event}</p>
                <p className="text-xs text-slate-400">{item.date}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Step 4: Financial Dashboard
const FinancialStep = () => (
  <div className="space-y-4 animate-fadeIn">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[
        { label: "Total Revenue", value: "$24,650", change: "+12%", color: "#22C55E" },
        { label: "Outstanding", value: "$3,200", change: "4 invoices", color: "#F59E0B" },
        { label: "Collected This Month", value: "$8,400", change: "+18%", color: "#3B82F6" },
        { label: "Avg. Invoice", value: "$1,230", change: "+5%", color: "#6366F1" },
      ].map((stat, i) => (
        <div key={i} className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500 mb-1">{stat.label}</p>
          <p className="text-xl font-bold text-slate-900">{stat.value}</p>
          <p className="text-xs font-medium mt-1" style={{ color: stat.color }}>{stat.change}</p>
        </div>
      ))}
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-[#F59E0B] rounded-lg flex items-center justify-center">
            <DollarSign className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-slate-900 text-sm">Recent Invoices</span>
        </div>
        <div className="space-y-3">
          {[
            { client: "Corey Gomez", amount: "$750", status: "Pending", statusColor: "text-[#F59E0B]" },
            { client: "Maria Lopez", amount: "$1,200", status: "Pending", statusColor: "text-[#F59E0B]" },
            { client: "Aisha Patel", amount: "$2,400", status: "Paid", statusColor: "text-[#22C55E]" },
            { client: "Jose Soto", amount: "$850", status: "Paid", statusColor: "text-[#22C55E]" },
          ].map((inv, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
              <div>
                <p className="text-sm font-medium text-slate-900">{inv.client}</p>
                <p className="text-xs text-slate-400">INV-{1042 - i}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-slate-900">{inv.amount}</p>
                <p className={`text-xs font-medium ${inv.statusColor}`}>{inv.status}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-[#22C55E] rounded-lg flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-slate-900 text-sm">Revenue Trend</span>
        </div>
        <div className="flex items-end gap-1.5 h-32">
          {[40, 55, 35, 65, 50, 75, 60, 80, 70, 90, 85, 95].map((h, i) => (
            <div key={i} className="flex-1 flex flex-col justify-end h-full">
              <div
                className="w-full rounded-t"
                style={{
                  height: `${h}%`,
                  backgroundColor: i >= 10 ? "#3B82F6" : i >= 8 ? "rgba(59,130,246,0.7)" : "rgba(59,130,246,0.35)",
                }}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2 text-[10px] text-slate-400">
          <span>Jan</span>
          <span>Mar</span>
          <span>Jun</span>
          <span>Sep</span>
          <span>Dec</span>
        </div>
      </div>
    </div>
  </div>
);

const STEPS = [
  {
    title: "Your Daily Command Center",
    subtitle: "See which clients need attention and where revenue opportunities exist — all in one view.",
    component: DashboardStep,
  },
  {
    title: "Never Miss a Follow-Up",
    subtitle: "Your prioritized queue shows overdue, due today, and upcoming follow-ups with one-click actions.",
    component: FollowUpStep,
  },
  {
    title: "Complete Client History",
    subtitle: "Every call, email, note, and invoice in one timeline. No more digging through scattered records.",
    component: ClientProfileStep,
  },
  {
    title: "Financial Clarity",
    subtitle: "Track invoices, payments, and revenue trends without complicated accounting software.",
    component: FinancialStep,
  },
];

export default function DemoModal({ open, onClose, onStartTrial }) {
  const [step, setStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showFinal, setShowFinal] = useState(false);

  const goToStep = useCallback((s) => {
    setShowFinal(false);
    setStep(s);
    setIsPlaying(true);
  }, []);

  const nextStep = useCallback(() => {
    if (showFinal) return;
    if (step >= TOTAL_STEPS - 1) {
      setShowFinal(true);
      setIsPlaying(false);
    } else {
      setStep(s => s + 1);
      setIsPlaying(true);
    }
  }, [step, showFinal]);

  const prevStep = useCallback(() => {
    if (showFinal) {
      setShowFinal(false);
      setStep(TOTAL_STEPS - 1);
      setIsPlaying(false);
    } else if (step > 0) {
      setStep(s => s - 1);
      setIsPlaying(true);
    }
  }, [step, showFinal]);

  // Auto-play
  useEffect(() => {
    if (!open || !isPlaying || showFinal) return;
    const timer = setTimeout(nextStep, AUTOPLAY_INTERVAL);
    return () => clearTimeout(timer);
  }, [open, isPlaying, step, showFinal, nextStep]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep(0);
      setIsPlaying(true);
      setShowFinal(false);
    }
  }, [open]);

  // Keyboard nav
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") nextStep();
      if (e.key === "ArrowLeft") prevStep();
      if (e.key === " ") { e.preventDefault(); setIsPlaying(p => !p); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, nextStep, prevStep, onClose]);

  if (!open) return null;

  const CurrentStep = showFinal ? null : STEPS[step]?.component;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      data-testid="demo-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#F7F8FA] rounded-2xl w-full max-w-4xl max-h-[90vh] mx-4 shadow-2xl flex flex-col overflow-hidden" data-testid="demo-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <img
                src="https://customer-assets.emergentagent.com/job_ops-portal-8/artifacts/a8dmjt8s_clientops-ios-1024.png"
                alt="ClientOps"
                className="w-6 h-6 rounded"
              />
              <span className="font-semibold text-slate-900 text-sm" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                ClientOps<sup className="text-[8px] text-slate-400 ml-0.5">&trade;</sup>
              </span>
            </div>
            <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Product Tour</span>
          </div>
          <div className="flex items-center gap-3">
            {!showFinal && (
              <button
                onClick={() => setIsPlaying(p => !p)}
                className="text-slate-400 hover:text-slate-600 transition-colors p-1"
                data-testid="demo-play-pause"
              >
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>
            )}
            <span className="text-xs text-slate-400">
              {showFinal ? "Done" : `${step + 1} / ${TOTAL_STEPS}`}
            </span>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition-colors p-1"
              data-testid="demo-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Progress */}
        {!showFinal && (
          <div className="px-6 pt-3">
            <ProgressBar current={step} total={TOTAL_STEPS} isPlaying={isPlaying} onStepClick={goToStep} />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {showFinal ? (
            <div className="flex flex-col items-center justify-center py-12 animate-fadeIn" data-testid="demo-final-screen">
              <div className="w-16 h-16 bg-[#3B82F6]/10 rounded-2xl flex items-center justify-center mb-6">
                <Zap className="w-8 h-8 text-[#3B82F6]" />
              </div>
              <h2
                className="text-2xl sm:text-3xl font-semibold text-slate-900 text-center mb-3"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                Start Managing Your Clients Smarter
              </h2>
              <p className="text-slate-500 text-center max-w-md mb-8">
                Join consultants who use ClientOps to stay organized, follow up consistently, and recover revenue.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => { onClose(); onStartTrial?.("starter"); }}
                  className="flex items-center justify-center gap-2 bg-[#3B82F6] text-white font-medium px-6 py-3 rounded-lg hover:bg-[#2563EB] transition-colors shadow-lg shadow-[#3B82F6]/25"
                  data-testid="demo-final-cta-trial"
                >
                  Start Free Trial <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    onClose();
                    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="flex items-center justify-center gap-2 bg-white text-slate-700 font-medium px-6 py-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                  data-testid="demo-final-cta-pricing"
                >
                  View Pricing
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-4">No credit card required</p>
            </div>
          ) : (
            <>
              <div className="mb-5">
                <h3
                  className="text-lg font-semibold text-slate-900 mb-1"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  data-testid="demo-step-title"
                >
                  {STEPS[step].title}
                </h3>
                <p className="text-sm text-slate-500">{STEPS[step].subtitle}</p>
              </div>
              {CurrentStep && <CurrentStep />}
            </>
          )}
        </div>

        {/* Footer nav */}
        {!showFinal && (
          <div className="flex items-center justify-between px-6 py-4 bg-white border-t border-slate-200">
            <button
              onClick={prevStep}
              disabled={step === 0}
              className={`flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg transition-colors ${
                step === 0
                  ? "text-slate-300 cursor-not-allowed"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              }`}
              data-testid="demo-prev-btn"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <button
              onClick={nextStep}
              className="flex items-center gap-1.5 text-sm font-medium text-white bg-[#3B82F6] hover:bg-[#2563EB] px-4 py-2 rounded-lg transition-colors"
              data-testid="demo-next-btn"
            >
              {step === TOTAL_STEPS - 1 ? "See Results" : "Next"} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
